import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';

export type ReportLoadErrorType = 'network' | 'timeout' | 'render' | 'cors';
export type ReportLinkErrorType = '404' | 'timeout' | 'network';

const LINK_PROBE_TIMEOUT_MS = 8_000;

export const classifyLoadError = (error: unknown): ReportLoadErrorType => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const normalized = message.toLowerCase();

  if (normalized.includes('timeout') || normalized.includes('timed out')) {
    return 'timeout';
  }
  if (normalized.includes('cors') || normalized.includes('cross-origin')) {
    return 'cors';
  }
  if (
    normalized.includes('network') ||
    normalized.includes('fetch') ||
    normalized.includes('failed to fetch')
  ) {
    return 'network';
  }
  return 'render';
};

export const trackReportLoadError = (
  reportId: string,
  error: unknown,
  errorType?: ReportLoadErrorType
): boolean => {
  if (!reportId) return false;

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Unknown report load error';

  return trackEvent('report_load_error', {
    report_id: reportId,
    schema_version: POSTHOG_SCHEMA_VERSION,
    error_type: errorType ?? classifyLoadError(error),
    error_message: message,
  });
};

const getDestinationDomain = (url: string): string | undefined => {
  try {
    return new URL(url, window.location.href).hostname;
  } catch {
    return undefined;
  }
};

const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

const probeLink = async (url: string): Promise<ReportLinkErrorType | null> => {
  if (url.startsWith('mailto:') || url.startsWith('tel:') || url.startsWith('#')) {
    return null;
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), LINK_PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
      redirect: 'follow',
      signal: controller.signal,
    });

    if (response.status === 404 || response.status === 410) {
      return '404';
    }
    return null;
  } catch (error) {
    if (controller.signal.aborted) {
      return 'timeout';
    }
    if (isSameOriginUrl(url)) {
      return 'network';
    }
    return null;
  } finally {
    window.clearTimeout(timer);
  }
};

export const trackReportLinkError = (input: {
  errorType: ReportLinkErrorType;
  linkUrl: string;
  sectionName?: string | null;
}): boolean => {
  const properties: Record<string, unknown> = {
    error_type: input.errorType,
    link_url: input.linkUrl,
  };
  if (input.sectionName) {
    properties.section_name = input.sectionName;
  }
  const domain = getDestinationDomain(input.linkUrl);
  if (domain) {
    properties.destination_domain = domain;
  }
  return trackEvent('report_link_error', properties);
};

export const startReportLinkErrorTracking = (
  root: ParentNode = document
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor) return;

    const href = anchor.href;
    if (!href) return;

    const section = anchor.closest('[data-report-section]') as HTMLElement | null;
    const sectionName =
      section?.dataset.reportSectionName ||
      section?.dataset.reportSection ||
      null;

    void probeLink(href).then((errorType) => {
      if (!errorType) return;
      trackReportLinkError({
        errorType,
        linkUrl: href,
        sectionName,
      });
    });
  };

  root.addEventListener('click', handleClick, true);

  return () => {
    root.removeEventListener('click', handleClick, true);
  };
};

export const startReportRenderErrorTracking = (
  reportId: string
): (() => void) => {
  if (!reportId || typeof window === 'undefined') {
    return () => undefined;
  }

  const handleError = (event: ErrorEvent) => {
    trackReportLoadError(reportId, event.error ?? event.message, 'render');
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    trackReportLoadError(reportId, event.reason, 'render');
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleRejection);
  };
};
